import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { useState } from "react";
import usePolicyForm from "../../hooks/usePolicyForm.tsx";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import moment from "moment/moment";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import {
  inputClass,
  labelClass,
  errorMessageClass,
} from "../../constants/formStyles";

const NewDocument = () => {
  const { jwt } = useAuth();
  const SZK_DOC_TYPE = "3";

  const actions = {
    0: "Приемане",
    1: "Смяна на агент",
    2: "Анулиране",
    3: "Връщане в ЗК",
    4: "Нулиране",
  };

  const documentTypes = {
    0: "Сметки",
    1: "Стикери",
    2: "Други ЦК",
    3: "СЗК",
  };

  const initialFormState = {
    action: 0,
    agent: "",
    insuranceCompany: "",
    startValue: "",
    endValue: "",
    documentType: "0",
    date: moment().format("DD/MM/YYYY"),
  };

  const [formState, setFormState] = useState(initialFormState);

  const [alert, setAlert] = useState<{
    message: string;
    type: FadeAlertType;
  } | null>(null);

  const { agentOptions, insuranceCompanies } = usePolicyForm();

  const handleInputChange = (key: string, value: string | number) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
  };

  const saveNewPolicyDocument = async () => {
    try {
      const response = await PolicyService.saveNewDocument(formState, jwt);

      setAlert({
        message: response,
        type: FadeAlertType.success,
      });

      setFormState(initialFormState);
    } catch (e: any) {
      setAlert({
        message: e.message,
        type: FadeAlertType.error,
      });
    }
  };

  const isSZK = String(formState.documentType) === SZK_DOC_TYPE;

  const szkPattern = /^[A-Za-z]{2}\d+$/; // Exactly 2 English letters + digits
  const numericPattern = /^\d+$/;

  const validateFormat = (val: string) => {
    if (!val) return true;
    return isSZK ? szkPattern.test(val) : numericPattern.test(val);
  };

  const isStartFormatInvalid = !validateFormat(formState.startValue);
  const isEndFormatInvalid = !validateFormat(formState.endValue);

  const isRangeInvalid = (() => {
    const { startValue, endValue } = formState;

    if (!startValue || !endValue) {
      return false;
    }

    if (isStartFormatInvalid || isEndFormatInvalid) {
      return false;
    }

    if (isSZK) {
      const startPrefix = startValue.substring(0, 2).toUpperCase();
      const endPrefix = endValue.substring(0, 2).toUpperCase();

      if (startPrefix !== endPrefix) {
        return true;
      }

      const startNum = Number(startValue.substring(2));
      const endNum = Number(endValue.substring(2));

      return endNum < startNum || endNum - startNum > 100;
    } else {
      const startNum = Number(startValue);
      const endNum = Number(endValue);

      return endNum < startNum || endNum - startNum > 100;
    }
  })();

  const isStartInputError = isStartFormatInvalid || isRangeInvalid;
  const isEndInputError = isEndFormatInvalid || isRangeInvalid;

  const isFormInvalid =
    isStartFormatInvalid ||
    isEndFormatInvalid ||
    isRangeInvalid ||
    !formState.startValue ||
    !formState.endValue;

  return (
    <>
      <Breadcrumb pageName="Нови документи" />

      {alert && (
        <FadeAlert
          alert={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <SectionHeader title="Нови документни" />
        <div className="mt-2 p-3 pt-1">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Изберете действие</label>
              <SelectGroup
                placeholder="Изберете действие"
                options={actions}
                value={String(formState.action)}
                onChange={(selectedValue: string) => {
                  handleInputChange("action", Number(selectedValue));
                }}
              />
            </div>

            <div>
              <label className={labelClass}>Тип документ</label>
              <SelectGroup
                placeholder="Изберете тип документ"
                options={documentTypes}
                value={String(formState.documentType)}
                onChange={(selectedValue: string) => {
                  handleInputChange("documentType", selectedValue);
                }}
              />
            </div>

            <div>
              <label className={labelClass}>От номер</label>
              <input
                type="text"
                value={formState.startValue}
                onChange={(e) =>
                  handleInputChange("startValue", e.target.value)
                }
                className={isStartInputError ? errorMessageClass : inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>До номер</label>
              <input
                type="text"
                value={formState.endValue}
                onChange={(e) => handleInputChange("endValue", e.target.value)}
                className={isEndInputError ? errorMessageClass : inputClass}
              />
            </div>

            {insuranceCompanies && (
              <div>
                <label className={labelClass}>Застрахователна компания</label>
                <SelectGroup
                  placeholder="Изберете ЗК"
                  value={formState.insuranceCompany}
                  options={insuranceCompanies}
                  onChange={(selectedValue: string) => {
                    handleInputChange("insuranceCompany", selectedValue);
                  }}
                />
              </div>
            )}

            {agentOptions && (
              <div>
                <label className={labelClass}>Агент</label>
                <SelectGroup
                  placeholder="Изберете агент"
                  value={formState.agent}
                  options={agentOptions}
                  onChange={(selectedValue: string) => {
                    handleInputChange("agent", selectedValue);
                  }}
                />
              </div>
            )}

            <div>
              <DatePicker
                label="Дата"
                onChange={(e) => handleInputChange("date", e.target.value)}
                value={formState.date}
                name="newPolicyDate"
              />
            </div>
          </div>

          <div className="mt-3 flex justify-center">
            <button
              disabled={isFormInvalid}
              onClick={(e) => {
                e.preventDefault();
                saveNewPolicyDocument();
              }}
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:bg-opacity-50"
            >
              Запис
            </button>
          </div>
        </div>
      </fieldset>
    </>
  );
};

export default NewDocument;
